





































import type { AuthenticationStep } from '@getaccept/lib-shared/src/authentication/types/authentication-step';
import type { AuthenticationStepInput } from '@getaccept/lib-shared/src/authentication/types/authentication-step-input';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { BrowserHelper } from '@getaccept/lib-shared/src/chat/helpers/browser.helper';
import { BackgroundType } from '@getaccept/lib-shared/src/entity/types/enums/background-type';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { BrandingHelper } from '@getaccept/lib-shared/src/signing-theme/helpers/branding.helper';
import { Toasts } from '@getaccept/lib-shared/src/toast';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { useDocumentStore } from './documents/store/document.store';
import { useEntityStore } from './entity/store/entity.store';
import { DocumentHelper } from './helpers/document.helper';
import Lobby from './pages/Lobby.vue';
import { pusherClient } from './pusher';
import { usePusherStore } from './pusher/store/pusher.store';
import { sessionNamespace } from './session/store/session.constants';
import { SessionAction, SessionGetter } from './session/store/session.types';
import store from './store/root.store';
import { RootAction } from './store/root.types';
import { useThemeStore } from './theme/theme.store';

export default defineComponent({
  components: {
    Lobby,
    Toasts,
  },
  setup(_, { root }) {
    const authId = computed(() => store.state.authId);
    const documentId = computed(() => store.state.documentId);
    const recipientId = computed(() => store.state.recipientId);
    const userId = computed(() => store.state.userId);
    const isPreview = computed(() => store.state.isPreview);
    const previewExpired = computed(() => store.state.previewExpired);

    const actionSetPusherConnected = (connected: boolean) =>
      store.dispatch(RootAction.SetPusherConnected, connected);

    const token = computed((): { jwt: string; exp: number } => store.state[sessionNamespace].token);
    const isCorrectPin = computed((): boolean => store.state[sessionNamespace].isCorrectPin);
    const sessionError = computed((): boolean => store.state[sessionNamespace].error);
    const authentication = computed(
      (): {
        submittingResponse: boolean;
        steps: AuthenticationStep[];
        answers: AuthenticationStepInput[];
      } => store.state[sessionNamespace].authentication
    );

    const nextAuthStep = computed(
      (): AuthenticationStep => store.getters[`${sessionNamespace}/${SessionGetter.NextAuthStep}`]
    );
    const documentRecalled = computed(
      (): boolean => store.getters[`${sessionNamespace}/${SessionGetter.DocumentRecalled}`]
    );
    const documentUnpublished = computed(
      (): boolean => store.getters[`${sessionNamespace}/${SessionGetter.DocumentUnpublished}`]
    );
    const consentSteps = computed(
      (): AuthenticationStep => store.getters[`${sessionNamespace}/${SessionGetter.ConsentSteps}`]
    );
    const gdprDeclined = computed(
      (): boolean => store.getters[`${sessionNamespace}/${SessionGetter.GDPRDeclined}`]
    );

    const actionLoadToken = (authStepsInput?: AuthenticationStepInput[]) =>
      store.dispatch(`${sessionNamespace}/${SessionAction.LoadToken}`, authStepsInput);

    const setToken = (payload: {
      token: {
        jwt: string;
        exp: number;
      };
      authStepsInput: AuthenticationStepInput[];
    }) => store.commit(`${sessionNamespace}/${SessionAction.LoadTokenSuccess}`, payload);

    const actionSubmitAnswer = (authStepsInput: AuthenticationStepInput[]) =>
      store.dispatch(`${sessionNamespace}/${SessionAction.SubmitAnswer}`, authStepsInput);

    const actionSendSmsCode = () =>
      store.dispatch(`${sessionNamespace}/${SessionAction.SendSmsCode}`);

    const actionSubmitSmsAnswer = (authStepsInput: AuthenticationStepInput[]) =>
      store.dispatch(`${sessionNamespace}/${SessionAction.SubmitSmsAnswer}`, authStepsInput);

    const actionSubmitQnaAnswer = (authStep: AuthenticationStepInput) =>
      store.dispatch(`${sessionNamespace}/${SessionAction.SubmitQnaAnswer}`, authStep);

    const actionSubmitConsent = (authStepsInput: AuthenticationStepInput[]) =>
      store.dispatch(`${sessionNamespace}/${SessionAction.SubmitConsent}`, authStepsInput);

    const actionDeclineConsent = () =>
      store.dispatch(`${sessionNamespace}/${SessionAction.DeclineConsent}`);

    const documentStore = useDocumentStore();
    const { document, isProcessing, error: documentError } = storeToRefs(documentStore);

    const documentAvailable = ref(false);
    const themeStore = useThemeStore();
    const { theme } = storeToRefs(themeStore);
    const entityStore = useEntityStore();
    const loading = ref(true);

    const hasValidToken = computed(() => {
      const notExpired: boolean =
        (token.value.exp && !moment().isAfter(moment.unix(token.value.exp))) || !token.value.exp;
      const tokenFound = !!token.value.jwt;
      return tokenFound && notExpired;
    });

    const watchToken = () => {
      if (hasValidToken.value) {
        loadEntity();
        loadPusher();
      }
    };

    watch(hasValidToken, watchToken);

    const showLobby = computed(
      () =>
        !!nextAuthStep.value ||
        !!consentSteps.value ||
        hasError.value ||
        documentRecalled.value ||
        documentExpired.value ||
        documentRejected.value ||
        documentUnpublished.value ||
        previewExpired.value ||
        gdprDeclined.value ||
        isProcessing.value
    );

    const pageTitle = computed(() => (document.value ? document.value.name : 'GetAccept'));
    const documentExpired = computed(() => DocumentHelper.getIsDocumentExpired(document.value));
    const hasFixedBackground = computed(
      () =>
        !theme.value ||
        theme.value.backgroundType === BackgroundType.Fixed ||
        !theme.value.backgroundImage
    );
    const documentRejected = computed(
      () => !!document.value && document.value.status === DocumentStatus.Rejected
    );

    const hasParams = computed(() => {
      if (isPreview.value && documentId.value) {
        return true;
      }
      return !!authId.value && !!documentId.value && (!!recipientId.value || !!userId.value);
    });

    const hasError = computed(() => sessionError.value || !hasParams.value || documentError.value);

    const safeBackground = computed(() =>
      BrandingHelper.getBackground(theme.value, BrowserHelper.isIE(navigator))
    );

    const unhandledrejectionHandler = (event: PromiseRejectionEvent) => {
      if (bugsnagClient) {
        bugsnagClient.notify(event);
      }
    };

    const watchRouteMeta = () => {
      const isUniversalRoute = window.location.href.includes('/share/d');

      if (isUniversalRoute && root.$route.meta.universal) {
        loading.value = false;
      }

      if (!isUniversalRoute) {
        loading.value = false;
      }
    };

    watch(() => root.$route.meta, watchRouteMeta, { immediate: true });

    const created = () => {
      window.addEventListener('unhandledrejection', unhandledrejectionHandler);
    };

    created();

    onBeforeUnmount(() => {
      window.removeEventListener('unhandledrejection', unhandledrejectionHandler);
    });

    const setDocumentTitle = () => {
      window.document.title = pageTitle.value;
    };

    const loadEntity = async () => {
      await entityStore.load();
      if (!documentRecalled.value && !documentUnpublished.value) {
        documentAvailable.value = true;
      }
    };

    const pusherStore = usePusherStore();

    const loadPusher = async () => {
      try {
        await pusherClient.connect(token.value.jwt);
        await pusherStore.subscribeToDocumentChannel(documentId.value);
        await pusherStore.subscribeToRecipientChannel(recipientId.value);
        actionSetPusherConnected(true);
      } catch (e) {
        console.error(e);
        if (bugsnagClient) {
          bugsnagClient.notify(e);
        }
      }
    };

    watch(
      hasValidToken,
      async () => {
        if (hasValidToken.value) {
          await loadPusher();
          loadEntity();
        }
      },
      { immediate: true }
    );

    const preventIosInputZoom = () => {
      if (navigator.userAgent.indexOf('iPhone') > -1) {
        window.document
          .querySelector('[name=viewport]')
          .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
      }
    };

    onMounted(async () => {
      preventIosInputZoom();
      if (hasParams.value) {
        themeStore.loadTheme(documentId.value);
      }
      if (!hasValidToken.value && hasParams.value) {
        await actionLoadToken([]);
      }
      setDocumentTitle();
    });

    return {
      token,
      authId,
      documentId,
      recipientId,
      setToken,
      safeBackground,
      hasFixedBackground,
      theme,
      themeStore,
      showLobby,
      consentSteps,
      nextAuthStep,
      documentRecalled,
      hasError,
      isCorrectPin,
      documentExpired,
      documentRejected,
      previewExpired,
      gdprDeclined,
      document,
      entityStore,
      authentication,
      actionSubmitAnswer,
      actionSubmitQnaAnswer,
      actionSendSmsCode,
      actionSubmitConsent,
      actionDeclineConsent,
      actionSubmitSmsAnswer,
      documentAvailable,
      loading,
      isProcessing,
    };
  },
});
